// ----------------------------------------------------------------------------
// Buy box v2 — capsule multiplier, tooltips, callout visibility, reviews scroll
// ----------------------------------------------------------------------------

import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import './_product-form-v2.scss';
var SEL = {
  root: '.product-form-v2',
  capsuleBtn: '.js-pfbv-capsule',
  supply: '[data-pfbv-supply-base]',
  priceDay: '[data-pfbv-price-per-day]',
  callout: '[data-pfbv-callout]',
  tooltipTrigger: '.js-pfbv-dosage-tooltip-trigger',
  tooltipPanel: '.js-pfbv-dosage-tooltip-panel',
  reviewsScroll: '.js-pfbv-scroll-reviews'
};
var ogPurchaseModeHint = new WeakMap();
var ogPurchaseComposedClickBound = false;

/**
 * OG opt-in / opt-out often live in shadow DOM — `closest()` from `e.target` misses them.
 * Use capture + `composedPath()` so we can still set purchase mode hints when OG uses explicit opt buttons.
 * Visibility for informational callouts follows `selling_plan` + checked radios (`isOneTimePurchaseMode`);
 * we do not infer mode from clicks on generic selling-plan rows (those can fire before radios update).
 */
function bindOgPurchaseComposedClicksOnce() {
  if (ogPurchaseComposedClickBound) return;
  ogPurchaseComposedClickBound = true;
  document.addEventListener('click', e => {
    var path = typeof e.composedPath === 'function' ? e.composedPath() : [e.target];
    var iamForm = path.find(n => {
      var _n$querySelector;
      return n instanceof HTMLElement && n.tagName === 'IAM-PRODUCT-FORM' && ((_n$querySelector = n.querySelector) === null || _n$querySelector === void 0 ? void 0 : _n$querySelector.call(n, SEL.root));
    });
    if (!iamForm) return;
    var ogHost = path.find(n => n instanceof HTMLElement && n.tagName === 'IAM-ORDERGROOVE-OFFER');
    if (!ogHost || !iamForm.contains(ogHost)) return;
    var optOut = path.some(n => n instanceof HTMLElement && n.tagName === 'OG-OPTOUT-BUTTON' && ogHost.contains(n));
    var optIn = path.some(n => n instanceof HTMLElement && n.tagName === 'OG-OPTIN-BUTTON' && ogHost.contains(n));
    var next = null;
    if (optOut) {
      next = 'one_time';
    } else if (optIn) {
      next = 'subscription';
    }
    if (!next) return;
    ogPurchaseModeHint.set(iamForm, next);
    window.requestAnimationFrame(() => syncCalloutsFromIamForm(iamForm));
  }, true);
}

// -----------------------------------------------------------------------------
// Money helpers
// -----------------------------------------------------------------------------

function formatMoneyCents(cents) {
  var n = Number(cents);
  if (Number.isNaN(n)) return '';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD'
  }).format(n / 100);
}

/**
 * Parse a currency string from OG fallback text into cents.
 */
function parseMoneyToCents(str) {
  if (!str) return null;
  // Keep digits, comma, dot, minus. Assume last separator is decimal if present.
  var cleaned = String(str).trim().replace(/[^\d.,-]/g, '');
  if (!cleaned) return null;
  var lastDot = cleaned.lastIndexOf('.');
  var lastComma = cleaned.lastIndexOf(',');
  var decIdx = Math.max(lastDot, lastComma);
  var normalized = cleaned;
  if (decIdx >= 0) {
    var intPart = cleaned.slice(0, decIdx).replace(/[.,]/g, '');
    var decPart = cleaned.slice(decIdx + 1).replace(/[.,]/g, '').slice(0, 2);
    normalized = "".concat(intPart, ".").concat(decPart);
  } else {
    normalized = cleaned.replace(/[.,]/g, '');
  }
  var num = Number(normalized);
  return Number.isNaN(num) ? null : Math.round(num * 100);
}
function parseCents(el) {
  var v = el === null || el === void 0 ? void 0 : el.getAttribute('data-price-cents');
  var n = Number(v);
  return Number.isNaN(n) ? null : n;
}

// -----------------------------------------------------------------------------
// Supply + delivery math (capsule multiplier aware)
// -----------------------------------------------------------------------------

function getCapsuleMultiplier(root) {
  return root.dataset.pfbvCapsulesPerDay === '2' ? 2 : 1;
}
function getSelectedSizeEl(root) {
  return root.querySelector('.product-form-v2__size-tab.is-selected') || root.querySelector('.product-form-v2__size-tab[aria-current="page"]');
}
function getEffectiveSupplyDays(root) {
  var _getSelectedSizeEl, _getSelectedSizeEl$qu;
  var mult = getCapsuleMultiplier(root);
  var selectedSupplyEl = (_getSelectedSizeEl = getSelectedSizeEl(root)) === null || _getSelectedSizeEl === void 0 || (_getSelectedSizeEl$qu = _getSelectedSizeEl.querySelector) === null || _getSelectedSizeEl$qu === void 0 ? void 0 : _getSelectedSizeEl$qu.call(_getSelectedSizeEl, '[data-pfbv-supply-base]');
  var base = Number(selectedSupplyEl === null || selectedSupplyEl === void 0 ? void 0 : selectedSupplyEl.getAttribute('data-pfbv-supply-base')) || Number(root.getAttribute('data-pfbv-supply-base')) || null;
  if (!base || Number.isNaN(base) || base <= 0) return null;
  return Math.max(1, Math.round(base / mult));
}
function getDeliveryDays(root) {
  var _getSelectedSizeEl2, _getSelectedSizeEl2$q;
  var selectedDeliveryEl = (_getSelectedSizeEl2 = getSelectedSizeEl(root)) === null || _getSelectedSizeEl2 === void 0 || (_getSelectedSizeEl2$q = _getSelectedSizeEl2.querySelector) === null || _getSelectedSizeEl2$q === void 0 ? void 0 : _getSelectedSizeEl2$q.call(_getSelectedSizeEl2, '[data-pfbv-delivery-days]');
  var n = Number(selectedDeliveryEl === null || selectedDeliveryEl === void 0 ? void 0 : selectedDeliveryEl.getAttribute('data-pfbv-delivery-days')) || Number(root.getAttribute('data-pfbv-delivery-days')) || null;
  return !n || Number.isNaN(n) || n <= 0 ? null : n;
}

// -----------------------------------------------------------------------------
// OrderGroove per-day syncing
// -----------------------------------------------------------------------------

function getOgCents(iamForm, mode) {
  var offer = iamForm.querySelector('iam-ordergroove-offer');
  if (!offer) return null;
  if (mode === 'subscription') {
    var el = offer.querySelector('.selling-plan__option:not(.selling-plan__option--one-time) og-price[subscription] [slot="fallback"]') || offer.querySelector('og-price[subscription] [slot="fallback"]');
    return parseMoneyToCents(el === null || el === void 0 ? void 0 : el.textContent);
  }
  var one = offer.querySelector('.selling-plan__option--one-time og-price.og-price--one-time[regular] [slot="fallback"]') || offer.querySelector('.selling-plan__option--one-time og-price.og-price--one-time [slot="fallback"]') || offer.querySelector('.selling-plan__option--one-time og-price[regular] [slot="fallback"]');
  return parseMoneyToCents(one === null || one === void 0 ? void 0 : one.textContent);
}
function syncOgPerDay(iamForm) {
  var root = iamForm.querySelector(SEL.root);
  if (!root) return;
  var offer = iamForm.querySelector('iam-ordergroove-offer');
  if (!offer) return;
  var effectiveDays = getEffectiveSupplyDays(root);
  var deliveryDays = getDeliveryDays(root);
  if (!effectiveDays) return;
  var subCents = getOgCents(iamForm, 'subscription');
  var oneCents = getOgCents(iamForm, 'one_time');
  if (subCents != null) {
    var subPerDayCents = Math.round(subCents / effectiveDays);
    // Update subscription row only
    offer.querySelectorAll('.selling-plan__option:not(.selling-plan__option--one-time) .per-day__price').forEach(el => {
      el.textContent = "".concat(formatMoneyCents(subPerDayCents), "/day");
    });
  }
  if (oneCents != null) {
    var onePerDayCents = Math.round(oneCents / effectiveDays);
    // Update one-time row only
    offer.querySelectorAll('.selling-plan__option--one-time .per-day__price').forEach(el => {
      el.textContent = "".concat(formatMoneyCents(onePerDayCents), "/day");
    });
  }
  if (deliveryDays) {
    // Label is shown in subscription UI (and sometimes upsell modal); keep it aligned with supply/delivery.
    offer.querySelectorAll('.selling-plan-per-day__label').forEach(el => {
      el.textContent = "".concat(effectiveDays, "-Day Supply (Delivers every ").concat(deliveryDays, " days)");
    });
  }
}
function ensureOgPerDayApplied(iamForm) {
  // OG hydrates asynchronously; do a short retry loop so per-day is correct on load.
  var MAX_TRIES = 20;
  var INTERVAL_MS = 200;
  var tries = 0;
  var tick = () => {
    var _iamForm$querySelecto, _offer$querySelector, _offer$querySelector2, _offer$querySelector3;
    tries += 1;
    var offer = (_iamForm$querySelecto = iamForm.querySelector) === null || _iamForm$querySelecto === void 0 ? void 0 : _iamForm$querySelecto.call(iamForm, 'iam-ordergroove-offer');
    var hasPrices = !!(offer !== null && offer !== void 0 && (_offer$querySelector = offer.querySelector) !== null && _offer$querySelector !== void 0 && _offer$querySelector.call(offer, 'og-price[subscription] [slot="fallback"]')) || !!(offer !== null && offer !== void 0 && (_offer$querySelector2 = offer.querySelector) !== null && _offer$querySelector2 !== void 0 && _offer$querySelector2.call(offer, '.selling-plan__option--one-time og-price[regular] [slot="fallback"]'));
    var hasPerDay = !!(offer !== null && offer !== void 0 && (_offer$querySelector3 = offer.querySelector) !== null && _offer$querySelector3 !== void 0 && _offer$querySelector3.call(offer, '.per-day__price'));
    if (offer && (hasPrices || hasPerDay)) {
      syncOgPerDay(iamForm);
    }
    if (tries < MAX_TRIES && (!hasPrices || !hasPerDay)) {
      window.setTimeout(tick, INTERVAL_MS);
    }
  };
  tick();
}

// -----------------------------------------------------------------------------
// Buy box v2 UI syncing
// -----------------------------------------------------------------------------

function updateCapsuleState(root, capsulesPerDay) {
  var mult = capsulesPerDay === 2 ? 2 : 1;
  root.dataset.pfbvCapsulesPerDay = String(capsulesPerDay);
  root.querySelectorAll(SEL.capsuleBtn).forEach(btn => {
    var v = Number(btn.getAttribute('data-capsules') || '1');
    btn.setAttribute('aria-selected', v === capsulesPerDay ? 'true' : 'false');
  });
  root.querySelectorAll(SEL.supply).forEach(el => {
    var base = Number(el.getAttribute('data-pfbv-supply-base'));
    if (Number.isNaN(base)) return;
    var days = Math.max(1, Math.round(base / mult));
    el.textContent = String(days);
  });
  root.querySelectorAll(SEL.priceDay).forEach(el => {
    var cents = parseCents(el);
    if (cents == null) return;
    var baseDays = Number(el.getAttribute('data-pfbv-supply-base'));
    if (Number.isNaN(baseDays) || baseDays <= 0) return;
    var effectiveDays = baseDays / mult;
    var perDay = cents / effectiveDays;
    el.textContent = formatMoneyCents(Math.round(perDay));
  });
  var iamForm = root.closest('iam-product-form');
  if (iamForm) {
    syncOgPerDay(iamForm);
  }
}
function syncCallouts(root, isOneTime) {
  var mode = isOneTime ? 'one_time' : 'subscription';

  // Per-row: subscription / one-time; "both" uses `data-pfbv-always-show` from Liquid so we never hide it.
  root.querySelectorAll(SEL.callout).forEach(el => {
    if (el.hasAttribute('data-pfbv-always-show')) {
      el.removeAttribute('hidden');
      return;
    }
    var ctx = (el.getAttribute('data-pfbv-callout') || '').trim().toLowerCase();
    var show = ctx === 'both' || ctx === mode;
    el.toggleAttribute('hidden', !show);
  });
}
function getSellingPlanInput(iamProductForm) {
  var _iamProductForm$query;
  return iamProductForm === null || iamProductForm === void 0 || (_iamProductForm$query = iamProductForm.querySelector) === null || _iamProductForm$query === void 0 ? void 0 : _iamProductForm$query.call(iamProductForm, 'input[name="selling_plan"]');
}
function ogOfferHasSellingUi(offer) {
  var _offer$querySelector4;
  return !!(offer !== null && offer !== void 0 && (_offer$querySelector4 = offer.querySelector) !== null && _offer$querySelector4 !== void 0 && _offer$querySelector4.call(offer, '.selling-plan'));
}

/**
 * Whether the customer is on one-time purchase for callout visibility.
 *
 * Notes:
 * - OG radios are often not queryable from document JS (shadow DOM), so we cannot rely on `:checked`.
 * - OG may keep `selling_plan` non-empty when one-time is selected — use `ogPurchaseModeHint` from opt-in/out clicks.
 * - If `selling_plan` is cleared, treat as one-time without needing a hint.
 * - Before OG renders (no `.selling-plan` yet), default to **subscription** to match Liquid defaults.
 */
function isOneTimePurchaseMode(iamProductForm) {
  var _iamProductForm$query2;
  var hinted = ogPurchaseModeHint.get(iamProductForm);
  if (hinted === 'one_time') return true;
  if (hinted === 'subscription') return false;
  var offer = iamProductForm === null || iamProductForm === void 0 || (_iamProductForm$query2 = iamProductForm.querySelector) === null || _iamProductForm$query2 === void 0 ? void 0 : _iamProductForm$query2.call(iamProductForm, 'iam-ordergroove-offer');
  var input = getSellingPlanInput(iamProductForm);
  if (offer) {
    var oneChecked = offer.querySelector('.selling-plan__option--one-time input[type="radio"]:checked');
    var subChecked = offer.querySelector('.selling-plan__option:not(.selling-plan__option--one-time) input[type="radio"]:checked');
    if (oneChecked) return true;
    if (subChecked) return false;
  }
  if (!ogOfferHasSellingUi(offer)) {
    return false;
  }
  if (!input) return false;
  if (!input.value || input.value === '') return true;
  return false;
}
function syncCalloutsFromIamForm(iamProductForm) {
  var _iamProductForm$query3;
  var root = iamProductForm === null || iamProductForm === void 0 || (_iamProductForm$query3 = iamProductForm.querySelector) === null || _iamProductForm$query3 === void 0 ? void 0 : _iamProductForm$query3.call(iamProductForm, SEL.root);
  if (!root) return;
  syncCallouts(root, isOneTimePurchaseMode(iamProductForm));
  syncOgPerDay(iamProductForm);
}

/**
 * Fallback: direct listeners on the hidden input (some code paths fire native events).
 * Primary sync uses `productForm:sellingPlan:changed` from product-form--selling-plans (DOM watch + poll).
 */
function bindSellingPlanInputFallback(root, form) {
  var input = form.querySelector('input[name="selling_plan"]');
  if (!input) return;
  var apply = () => {
    var iamForm = root.closest('iam-product-form');
    if (iamForm) syncCalloutsFromIamForm(iamForm);
  };
  input.addEventListener('change', apply);
  input.addEventListener('input', apply);
}
function bindOgOfferUiResync(iamForm) {
  bindOgPurchaseComposedClicksOnce();
  var offer = iamForm.querySelector('iam-ordergroove-offer');
  if (!offer || offer.dataset.pfbvOgUiBound === 'true') return;
  offer.dataset.pfbvOgUiBound = 'true';
}
function bindDosageTooltip(root) {
  var trigger = root.querySelector(SEL.tooltipTrigger);
  var panel = root.querySelector(SEL.tooltipPanel);
  if (!trigger || !panel) return;
  var close = () => panel.classList.remove('is-open');
  trigger.addEventListener('click', e => {
    e.preventDefault();
    e.stopPropagation();
    var open = !panel.classList.contains('is-open');
    panel.classList.toggle('is-open', open);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
  document.addEventListener('click', e => {
    if (!panel.classList.contains('is-open')) return;
    if (trigger.contains(e.target) || panel.contains(e.target)) return;
    close();
    trigger.setAttribute('aria-expanded', 'false');
  });
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      close();
      trigger.setAttribute('aria-expanded', 'false');
    }
  });
}

// -----------------------------------------------------------------------------
// Init + event wiring
// -----------------------------------------------------------------------------

function initProductFormV2(root) {
  var form = root.closest('form');
  if (!form) return;
  var capsules = Number(root.dataset.pfbvCapsulesPerDay || '2');
  if (capsules !== 2) capsules = 1;
  root.querySelectorAll(SEL.capsuleBtn).forEach(btn => {
    btn.addEventListener('click', () => {
      var v = Number(btn.getAttribute('data-capsules') || '1');
      updateCapsuleState(root, v === 2 ? 2 : 1);
    });
  });
  updateCapsuleState(root, capsules);
  bindSellingPlanInputFallback(root, form);
  bindDosageTooltip(root);
  root.querySelectorAll('.js-pfbv-info-callout').forEach(row => {
    row.addEventListener('click', e => e.stopPropagation());
  });
  var iamForm = root.closest('iam-product-form');
  if (iamForm) {
    bindOgOfferUiResync(iamForm);
    syncCalloutsFromIamForm(iamForm);
  }
}
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll(SEL.root).forEach(initProductFormV2);
});
document.addEventListener('shopify:section:load', e => {
  var _el$querySelector;
  var el = e.target;
  if (el !== null && el !== void 0 && (_el$querySelector = el.querySelector) !== null && _el$querySelector !== void 0 && _el$querySelector.call(el, SEL.root)) {
    el.querySelectorAll(SEL.root).forEach(initProductFormV2);
  }
});
iamotaEventHandler.subscribe('productForm:sellingPlan:changed', eventData => {
  var _eventData$_options, _iamForm$querySelecto2;
  var iamForm = (_eventData$_options = eventData._options) === null || _eventData$_options === void 0 ? void 0 : _eventData$_options.context;
  if (!(iamForm !== null && iamForm !== void 0 && (_iamForm$querySelecto2 = iamForm.querySelector) !== null && _iamForm$querySelecto2 !== void 0 && _iamForm$querySelecto2.call(iamForm, SEL.root))) return;
  syncCalloutsFromIamForm(iamForm);
}, {
  source: "product-form-v2.js"
});
iamotaEventHandler.subscribe('productForm:sellingPlanInput:changed', eventData => {
  var _eventData$_options2, _iamForm$querySelecto3;
  var iamForm = (_eventData$_options2 = eventData._options) === null || _eventData$_options2 === void 0 ? void 0 : _eventData$_options2.context;
  if (!(iamForm !== null && iamForm !== void 0 && (_iamForm$querySelecto3 = iamForm.querySelector) !== null && _iamForm$querySelecto3 !== void 0 && _iamForm$querySelecto3.call(iamForm, SEL.root))) return;
  syncCalloutsFromIamForm(iamForm);
}, {
  source: "product-form-v2.js"
});
document.addEventListener('og-receive-offer', () => {
  document.querySelectorAll('iam-product-form').forEach(iamForm => {
    var _iamForm$querySelecto4;
    if (!((_iamForm$querySelecto4 = iamForm.querySelector) !== null && _iamForm$querySelecto4 !== void 0 && _iamForm$querySelecto4.call(iamForm, SEL.root))) return;
    bindOgOfferUiResync(iamForm);
    syncCalloutsFromIamForm(iamForm);
  });
});
iamotaEventHandler.subscribe('productForm:init', eventData => {
  var iamForm = eventData.iamForm;
  syncCalloutsFromIamForm(iamForm);
  ensureOgPerDayApplied(iamForm);
}, {
  priority: 95,
  source: "product-form-v2.js"
});
iamotaEventHandler.subscribe('productForm:variant:changed', eventData => {
  var iamForm = eventData.iamProductForm;
  ogPurchaseModeHint.delete(iamForm);
  syncCalloutsFromIamForm(iamForm);
}, {
  source: "product-form-v2.js"
});